"use client";

import { useState } from "react";
import * as z from "zod";

import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/shadcn/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { Input } from "@/components/shadcn/input";
import { cn, validatePhoneNumber } from "@/lib/utils";

import { PhoneInput } from "@/components/app-ui/phone-input";
import { Checkbox } from "@/components/shadcn/checkbox";
import { Textarea } from "@/components/shadcn/textarea";
import axios from "axios";
import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { type Value } from "react-phone-number-input";
import { Button } from "./shadcn/button";

// Messages come from the `helpForms` catalog, so the schema is built per render.
const buildFormSchema = (t: (key: string) => string) =>
    z.object({
        name: z
            .string()
            .min(2, { message: t("nameRequired") })
            .max(19),
        surname: z.string().min(2, { message: "Soyisim gereklidir" }).max(19),
        email: z
            .string()
            .min(1, { message: "E-posta gereklidir." })
            .email(t("emailRequired")),
        phone: z
            .string()
            .transform((val) => val)
            .pipe(
                z
                    .string()
                    .refine(
                        (val) => validatePhoneNumber(val.replace(/\s+/g, "")),
                        {
                            message: t("phoneInvalid"),
                        },
                    ),
            ),
        message: z.string().min(10, { message: "Mesaj gereklidir" }).max(500),

        toc: z.literal<boolean>(true, {
            errorMap: () => ({
                message: t("consentRequired"),
            }),
        }),
    });

type ValidationSchema = z.infer<ReturnType<typeof buildFormSchema>>;

export default function BizeYazin() {
    const t = useTranslations("helpForms");
    const formSchema = buildFormSchema(t);
    const router = useRouter();

    const [phoneNumber, setPhoneNumber] = useState<Value>();
    const [formDisplay, setFormDisplay] = useState<boolean>(true);

    //check if product not has default values and redirect to premium page

    const form = useForm<ValidationSchema>({
        resolver: zodResolver(formSchema),
        defaultValues: { name: "", surname: "", email: "" },
    });
    const {
        control,
        formState: { errors },
    } = form;

    const onSubmitHandler = async (values: ValidationSchema) => {
        const { data: responseData } = await axios.post(
            "/api/support/post-bize-yazin-form",
            values,
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        );

        if (responseData.message === "success") {
            setFormDisplay(false);
        }
    };

    return (
        <Form {...form}>
            <div
                className={`${formDisplay == true ? "hidden" : "visible"} flex h-full flex-col items-center justify-center`}
            >
                <Check className="text-primary font-bold" size={48} />
                <h2
                    className={`text-primary flex items-center justify-center text-2xl font-semibold`}
                >
                    {t("messageReceived")}
                </h2>
            </div>
            <form
                className={`${formDisplay == true ? "visible" : "hidden"} flex flex-col`}
                onSubmit={form.handleSubmit(onSubmitHandler)}
            >
                <div className="mt-8 grid gap-x-4 lg:mt-0 lg:grid-cols-2 lg:grid-cols-[48%_50%]">
                    <FormField
                        control={control}
                        name="name"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-c-primary-marine-blue flex items-center justify-between">
                                    Ad
                                    <FormMessage>
                                        {errors.name?.message}
                                    </FormMessage>
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        className={cn(
                                            "placeholder:text-c-neutral-cool-gray border-c-neutral-light-gray text-c-primary-marine-blue w-[100%] placeholder:font-medium",
                                            {
                                                "border-c-primary-strawberry-red":
                                                    errors.name?.message,
                                            },
                                        )}
                                        {...field}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={control}
                        name="surname"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-c-primary-marine-blue flex items-center justify-between">
                                    Soyad
                                    <FormMessage>
                                        {errors.surname?.message}
                                    </FormMessage>
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        className={cn(
                                            "placeholder:text-c-neutral-cool-gray border-c-neutral-light-gray text-c-primary-marine-blue placeholder:font-medium",
                                            {
                                                "border-c-primary-strawberry-red":
                                                    errors.surname?.message,
                                            },
                                        )}
                                        {...field}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={control}
                        name="email"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-c-primary-marine-blue flex items-center justify-between">
                                    E-posta
                                    <FormMessage>
                                        {errors.email?.message}
                                    </FormMessage>
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        className={cn(
                                            "placeholder:text-c-neutral-cool-gray border-c-neutral-light-gray text-c-primary-marine-blue placeholder:font-medium",
                                            {
                                                "border-c-primary-strawberry-red":
                                                    errors.email?.message,
                                            },
                                        )}
                                        {...field}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={control}
                        name="phone"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-c-primary-marine-blue flex items-center justify-between">
                                    Cep telefonu
                                    <FormMessage>
                                        {errors.phone?.message}
                                    </FormMessage>
                                </FormLabel>
                                <FormControl>
                                    <PhoneInput
                                        {...field}
                                        international={true}
                                        defaultCountry="TR"
                                        maxLength={17}
                                        type="tel"
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </div>

                <div className="grid w-[100%] grid-cols-1 gap-4">
                    <FormField
                        control={control}
                        name="message"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-c-primary-marine-blue flex items-center justify-between">
                                    {t("yourMessage")}
                                    <FormMessage>
                                        {errors.message?.message}
                                    </FormMessage>
                                </FormLabel>
                                <FormControl>
                                    <Textarea
                                        className={cn(
                                            "placeholder:text-c-neutral-cool-gray border-c-neutral-light-gray text-c-primary-marine-blue bg-white placeholder:font-medium",
                                            {
                                                "border-strawberry-red":
                                                    errors.message?.message,
                                            },
                                        )}
                                        {...field}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={control}
                        name="toc"
                        render={({ field }) => (
                            <FormItem>
                                <div className="flex space-x-2">
                                    <FormControl id="toc">
                                        <Checkbox
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    </FormControl>
                                    <FormLabel
                                        className="text-sm"
                                        htmlFor="toc"
                                    >
                                        <FormMessage>
                                            {errors.toc?.message}
                                        </FormMessage>
                                        <Link
                                            target="_blank"
                                            href={
                                                "/sozlesmeler/aydinlatma-metni-web-iletisim-formu"
                                            }
                                            className="text-primary hover:text-primary-foreground"
                                        >
                                            {t("consentLink")}
                                        </Link>
                                        {t("consentSuffixLong")}
                                    </FormLabel>
                                </div>
                            </FormItem>
                        )}
                    />
                </div>
                <div className="flex justify-end">
                    <Button
                        onClick={form.handleSubmit(onSubmitHandler)}
                        className="mt-8 w-40"
                    >
                        {t("send")}
                    </Button>
                </div>
            </form>
        </Form>
    );
}
